// email.js — Phase 3.4: Email System UI
// Audience builder · Voice selector · Compose + bulk send

import { apiFetch } from './utils.js';
import { state } from './state.js';

let selectedRecipients = [];
let currentVoice = 'ryan';
let fromAccount = 'wmm';
let audienceFilter = 'this_year'; // 'all' | 'this_year'

export function initEmail() {
  window.renderEmailView = renderEmailView;
}

export async function renderEmailView() {
  const container = document.getElementById('view-inbox');
  if (!container) return;

  const [clients, contacts] = await Promise.all([
    apiFetch('/api/v1/clients').catch(() => []),
    apiFetch('/api/contacts').catch(() => [])
  ]);

  selectedRecipients = [];

  const currentYear = new Date().getFullYear();

  // Only include entries that have an email address
  const allEmailableClients   = clients.filter(c => c.email && c.email.trim());
  const emailableContacts     = contacts.filter(c => c.email && c.email.trim());

  const emailableClients = audienceFilter === 'this_year'
    ? allEmailableClients.filter(c => c.latest_season_year === currentYear)
    : allEmailableClients;

  // Group by ensemble_type for "By Program" (email-able clients only)
  const programs = [...new Set(emailableClients.map(c => c.ensemble_type).filter(Boolean))];
  // Roles from contacts (email-able only)
  const roles = [...new Set(emailableContacts.map(c => c.role).filter(Boolean))].sort();

  container.innerHTML = `
    <div class="email-view">
      <div class="email-view-header">
        <h2>Email</h2>
      </div>
      <div class="email-body">
        <div class="audience-builder" id="audience-builder">
          <div class="audience-header">
            <span class="audience-title">Audience</span>
            <span class="audience-count" id="audience-count">0 selected</span>
          </div>
          <div class="audience-filter-row">
            <button class="audience-filter-btn ${audienceFilter === 'this_year' ? 'active' : ''}"
                    onclick="window.setAudienceFilter('this_year')">
              ${currentYear} Season
            </button>
            <button class="audience-filter-btn ${audienceFilter === 'all' ? 'active' : ''}"
                    onclick="window.setAudienceFilter('all')">
              All Clients
            </button>
          </div>

          <!-- Directors -->
          <div class="audience-category">
            <div class="category-header" onclick="toggleCat('directors')">
              <span class="cat-arrow" id="arrow-directors">▼</span>
              <span class="cat-label">Directors</span>
              <span class="cat-count">${emailableClients.length}</span>
            </div>
            <div class="category-body" id="body-directors">
              <div class="cat-select-all">
                <label class="check-label">
                  <input type="checkbox" onchange="selectAllInCategory('directors', this.checked)">
                  All directors
                </label>
              </div>
              ${emailableClients.map(c => `
                <label class="check-label recipient-check">
                  <input type="checkbox"
                         value="${c.id}"
                         data-name="${escAttr(c.name)}"
                         data-email="${escAttr(c.email || '')}"
                         data-client-id="${c.id}"
                         data-type="director"
                         onchange="updateRecipient(this)">
                  ${c.name}
                  ${c.org_name ? `<span class="check-meta">${c.org_name}</span>` : ''}
                </label>
              `).join('')}
            </div>
          </div>

          <!-- By Role -->
          <div class="audience-category">
            <div class="category-header" onclick="toggleCat('role')">
              <span class="cat-arrow collapsed" id="arrow-role">▶</span>
              <span class="cat-label">By Role</span>
              <span class="cat-count">${emailableContacts.length}</span>
            </div>
            <div class="category-body collapsed" id="body-role">
              ${roles.length === 0 ? '<div class="empty-state">No contacts with email addresses</div>' : roles.map(role => {
                const inRole = emailableContacts.filter(c => c.role === role);
                return `
                  <div class="role-group">
                    <label class="check-label role-group-label">
                      <input type="checkbox" onchange="selectRole('${escAttr(role)}', this.checked)">
                      ${role} <span class="check-meta">${inRole.length}</span>
                    </label>
                    ${inRole.map(c => `
                      <label class="check-label check-indented">
                        <input type="checkbox"
                               value="${c.id}"
                               data-name="${escAttr(c.name)}"
                               data-email="${escAttr(c.email || '')}"
                               data-type="collaborator"
                               onchange="updateRecipient(this)">
                        ${c.name}
                      </label>
                    `).join('')}
                  </div>
                `;
              }).join('')}
            </div>
          </div>

          <!-- By Program (ensemble type) -->
          <div class="audience-category">
            <div class="category-header" onclick="toggleCat('program')">
              <span class="cat-arrow collapsed" id="arrow-program">▶</span>
              <span class="cat-label">By Program</span>
              <span class="cat-count">${programs.length}</span>
            </div>
            <div class="category-body collapsed" id="body-program">
              ${programs.map(prog => {
                const inProg = emailableClients.filter(c => c.ensemble_type === prog);
                return `
                  <label class="check-label">
                    <input type="checkbox" onchange="selectProgram('${escAttr(prog)}', this.checked)">
                    ${prog.replace(/_/g, ' ')} <span class="check-meta">${inProg.length}</span>
                  </label>
                `;
              }).join('')}
            </div>
          </div>

          <div class="selected-pills-area" id="selected-pills-area"></div>
        </div>

        ${renderComposePanel()}
      </div>
    </div>
  `;

  setupEmailHandlers();
}

function renderComposePanel() {
  return `
    <div class="compose-panel">
      <div class="compose-from-row">
        <span class="compose-label">From:</span>
        <div class="from-toggle">
          <button class="from-btn active" data-account="wmm" onclick="setFromAccount('wmm', this)">Ryan</button>
          <button class="from-btn" data-account="david" onclick="setFromAccount('david', this)">David</button>
        </div>
      </div>

      <div class="compose-to-row">
        <span class="compose-label">To:</span>
        <div class="compose-to-pills" id="compose-to-pills">
          <span class="to-placeholder">Select recipients from audience builder</span>
        </div>
      </div>

      <div class="compose-subject-row">
        <input type="text" id="compose-subject" class="compose-subject" placeholder="Subject">
      </div>

      <div class="voice-selector">
        <span class="compose-label">Voice:</span>
        <div class="voice-options">
          <button class="voice-btn active" data-voice="ryan" onclick="setVoice('ryan', this)">Ryan's Voice</button>
          <button class="voice-btn" data-voice="david" onclick="setVoice('david', this)">David Thorne</button>
          <button class="voice-btn" data-voice="own" onclick="setVoice('own', this)">Write My Own</button>
        </div>
      </div>

      <div class="ai-context-bar" id="ai-context-bar">
        <span class="ai-context-icon">✦</span>
        <span class="ai-context-text" id="ai-context-text">Ready to draft. Add context or click Regenerate.</span>
        <button class="ai-regenerate-btn" onclick="generateEmailDraft()">Regenerate</button>
      </div>

      <textarea id="compose-body" class="compose-body" placeholder="Your email will appear here..."></textarea>

      <div class="compose-footer">
        <button class="btn-send" id="btn-send" onclick="sendComposedEmail()">
          Send to <span id="send-count">0</span> →
        </button>
      </div>
    </div>
  `;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function escAttr(str) {
  return String(str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function refreshRecipientDisplay() {
  const countEl    = document.getElementById('audience-count');
  const pillsEl    = document.getElementById('compose-to-pills');
  const sendCountEl = document.getElementById('send-count');

  if (countEl)     countEl.textContent = `${selectedRecipients.length} selected`;
  if (sendCountEl) sendCountEl.textContent = selectedRecipients.length;

  if (pillsEl) {
    if (selectedRecipients.length === 0) {
      pillsEl.innerHTML = '<span class="to-placeholder">Select recipients from audience builder</span>';
    } else {
      pillsEl.innerHTML = selectedRecipients.map(r => `
        <span class="recipient-pill">
          ${r.name}
          <button onclick="removeRecipient('${r.id}','${r.type}')" class="pill-remove">×</button>
        </span>
      `).join('');
    }
  }
}

function setupEmailHandlers() {
  // keyboard: enter in subject focuses body
  document.getElementById('compose-subject')?.addEventListener('keydown', e => {
    if (e.key === 'Enter') document.getElementById('compose-body')?.focus();
  });
}

window.setAudienceFilter = function(filter) {
  audienceFilter = filter;
  selectedRecipients = [];
  renderEmailView();
};

// ── Window-level handlers ──────────────────────────────────────────────────

window.toggleCat = function(catId) {
  const body  = document.getElementById(`body-${catId}`);
  const arrow = document.getElementById(`arrow-${catId}`);
  if (!body) return;
  body.classList.toggle('collapsed');
  arrow.classList.toggle('collapsed');
  arrow.textContent = body.classList.contains('collapsed') ? '▶' : '▼';
};

window.updateRecipient = function(checkbox) {
  if (checkbox.checked) {
    const exists = selectedRecipients.find(r => r.id == checkbox.value && r.type === checkbox.dataset.type);
    if (!exists) {
      selectedRecipients.push({
        id: checkbox.value,
        name: checkbox.dataset.name,
        email: checkbox.dataset.email,
        client_id: checkbox.dataset.clientId || null,
        type: checkbox.dataset.type,
      });
    }
  } else {
    selectedRecipients = selectedRecipients.filter(
      r => !(r.id == checkbox.value && r.type === checkbox.dataset.type)
    );
  }
  refreshRecipientDisplay();
};

window.selectAllInCategory = function(catId, checked) {
  document.querySelectorAll(`#body-${catId} input[type="checkbox"][data-email]`).forEach(cb => {
    cb.checked = checked;
    if (checked) {
      if (!selectedRecipients.find(r => r.id == cb.value)) {
        selectedRecipients.push({ id: cb.value, name: cb.dataset.name, email: cb.dataset.email, client_id: cb.dataset.clientId || null, type: cb.dataset.type });
      }
    } else {
      selectedRecipients = selectedRecipients.filter(r => r.id != cb.value);
    }
  });
  refreshRecipientDisplay();
};

window.selectRole = function(role, checked) {
  document.querySelectorAll(`#body-role input[data-type="collaborator"]`).forEach(cb => {
    if (cb.closest('.role-group')?.querySelector('.role-group-label input')?.closest('label')?.textContent.trim().startsWith(role)) {
      cb.checked = checked;
      if (checked) {
        if (!selectedRecipients.find(r => r.id == cb.value)) {
          selectedRecipients.push({ id: cb.value, name: cb.dataset.name, email: cb.dataset.email, type: cb.dataset.type });
        }
      } else {
        selectedRecipients = selectedRecipients.filter(r => r.id != cb.value);
      }
    }
  });
  refreshRecipientDisplay();
};

window.selectProgram = function(prog, checked) {
  // Find director checkboxes whose org_name matches; simpler: match by data stored in state
  // We select all directors matching this ensemble_type from the Directors list
  document.querySelectorAll('#body-directors input[data-type="director"]').forEach(cb => {
    // We stored org_name in the label text but not as a data attr; use a workaround:
    // re-check against the clients array in state
    const client = (state.clients || []).find(c => String(c.id) === cb.value && c.email);
    if (client && client.ensemble_type === prog) {
      cb.checked = checked;
      if (checked) {
        if (!selectedRecipients.find(r => r.id == cb.value)) {
          selectedRecipients.push({ id: cb.value, name: cb.dataset.name, email: cb.dataset.email, client_id: cb.dataset.clientId || null, type: 'director' });
        }
      } else {
        selectedRecipients = selectedRecipients.filter(r => r.id != cb.value);
      }
    }
  });
  refreshRecipientDisplay();
};

window.removeRecipient = function(id, type) {
  selectedRecipients = selectedRecipients.filter(r => !(r.id == id && r.type === type));
  const cb = document.querySelector(`input[value="${id}"][data-type="${type}"]`);
  if (cb) cb.checked = false;
  refreshRecipientDisplay();
};

window.setVoice = function(voice, btn) {
  currentVoice = voice;
  document.querySelectorAll('.voice-btn').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');
};

window.setFromAccount = function(account, btn) {
  fromAccount = account;
  document.querySelectorAll('.from-btn').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');
};

window.generateEmailDraft = async function() {
  if (currentVoice === 'own') return;
  if (selectedRecipients.length === 0) { alert('Select recipients first'); return; }

  const subject    = document.getElementById('compose-subject')?.value;
  const contextEl  = document.getElementById('ai-context-text');
  if (contextEl) contextEl.textContent = 'Generating…';

  try {
    const response = await apiFetch('/api/email/draft', {
      method: 'POST',
      body: JSON.stringify({ voice: currentVoice, recipients: selectedRecipients, subject })
    });
    const bodyEl = document.getElementById('compose-body');
    if (bodyEl) bodyEl.value = response.draft || '';
    if (contextEl) contextEl.textContent = response.context_summary || 'Draft generated.';
  } catch (err) {
    // Expected 404 until Session 10
    if (contextEl) contextEl.textContent = 'AI drafting available in Session 10 — write manually for now.';
  }
};

window.sendComposedEmail = async function() {
  if (selectedRecipients.length === 0) { alert('Select at least one recipient'); return; }
  const subject = document.getElementById('compose-subject')?.value?.trim();
  const body    = document.getElementById('compose-body')?.value?.trim();
  if (!subject || !body) { alert('Subject and body are required'); return; }

  const noEmail = selectedRecipients.filter(r => !r.email);
  if (noEmail.length > 0) {
    const names = noEmail.map(r => r.name).join(', ');
    if (!confirm(`${noEmail.length} recipient(s) have no email address and will be skipped: ${names}\n\nContinue?`)) return;
  }

  const btn = document.getElementById('btn-send');
  if (btn) btn.disabled = true;

  try {
    const result = await apiFetch('/api/email/send-bulk', {
      method: 'POST',
      body: JSON.stringify({ recipients: selectedRecipients, subject, body, from_account: fromAccount, voice: currentVoice })
    });
    alert(`Sent to ${result.sent_count} recipient(s)${result.results?.filter(r=>!r.success).length ? `\n${result.results.filter(r=>!r.success).length} failed` : ''}`);
    document.getElementById('compose-subject').value = '';
    document.getElementById('compose-body').value = '';
    selectedRecipients = [];
    refreshRecipientDisplay();
  } catch (err) {
    alert('Send failed: ' + err.message);
  } finally {
    if (btn) btn.disabled = false;
  }
};
